import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export const CASE_PATIENT_PARAM = 'casePatient';
export const CASE_SCAN_PARAM = 'caseScan';

/**
 * Which case the console has open in its side panel. It lives in the query string rather
 * than in a store so a view can be linked ("this patient, this scan") and so the browser
 * back button closes the panel instead of leaving the admin screen — the same deal the
 * pipeline filters already have.
 *
 * Every write copies the current params, so a page's own filters survive opening a case.
 */
export function useAdminCaseSelection() {
  const [searchParams, setSearchParams] = useSearchParams();

  const patientId = searchParams.get(CASE_PATIENT_PARAM);
  const scanId = searchParams.get(CASE_SCAN_PARAM);

  const openCase = useCallback(
    (nextPatientId: string, nextScanId?: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(CASE_PATIENT_PARAM, nextPatientId);
          if (nextScanId) next.set(CASE_SCAN_PARAM, nextScanId);
          else next.delete(CASE_SCAN_PARAM);
          return next;
        },
        // A new case is a place you can come back from; picking a scan inside it is not.
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const selectScan = useCallback(
    (nextScanId: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(CASE_SCAN_PARAM, nextScanId);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closeCase = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(CASE_PATIENT_PARAM);
        next.delete(CASE_SCAN_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return { patientId, scanId, openCase, selectScan, closeCase };
}

export default useAdminCaseSelection;
